import { ExcludeBaseIcon } from '@tableau/icons/ExcludeBaseIcon';
import { NotificationCheckIcon } from '@tableau/icons/NotificationCheckIcon';
import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { Chip, ChipCSSVariables, ChipProps } from './Chip';

const SuccessSvg = NotificationCheckIcon({ size: 12 })!;
const InfoSvg = NotificationInfoOutlineIcon({ size: 14 })!;
/* eslint-disable max-len */
const WarningSvg = <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14">
  <path fill="currentColor" d="M6.20631136,0.486631016 C6.57745685,-0.162210339 7.50532059,-0.162210339 7.78367971,0.486631016 L13.9075804,11.6096257 C14.1859395,12.258467 13.814794,13 13.072503,13 L0.917488052,13 C0.267983436,13 -0.195948433,12.258467 0.0824106882,11.6096257 L6.20631136,0.486631016 Z M6.39188411,3.73083779 L6.39188411,9.29233512 L7.78367971,9.29233512 L7.78367971,3.73083779 L6.39188411,3.73083779 Z M6.39188411,10.2192513 L6.39188411,11.1461676 L7.78367971,11.1461676 L7.78367971,10.2192513 L6.39188411,10.2192513 Z"/>
</svg> as React.ReactSVGElement; // leaving this one here because the corresponding icon from the icons library has fixed colors
/* eslint-enable max-len */
const ErrorSvg = ExcludeBaseIcon({ size: 14 })!;

const ErrorChipCSSVariables: ChipCSSVariables = Object.freeze({
  '--chip-background-default': '#FEDED8',
  '--chip-background-disabled': 'transparent',
  '--chip-background-hover': '#FEB8AB',
  '--chip-background-press': '#FE8F7D',
  '--chip-content-default': '#BA0517',
  '--chip-content-disabled': '#C9C9C9',
  '--chip-content-hover': '#8E030F',
  '--chip-content-press': '#640103',
});

// chipCSSVariables would be required when we start supporting the other types
type FeedbackChipResource = {icon: React.ReactSVGElement, iconWidth: number, getPrefix: () => string, chipCSSVariables?: ChipCSSVariables };
const ResourceMap: { [key in 'success' | 'info' | 'warning' | 'error']: FeedbackChipResource } = {
  success: { icon: SuccessSvg, iconWidth: 12, getPrefix: Messages.successToastPrefix },
  info: { icon: InfoSvg, iconWidth: 14, getPrefix: Messages.infoToastPrefix },
  warning: { icon: WarningSvg, iconWidth: 14, getPrefix: Messages.warningToastPrefix },
  error: { icon: ErrorSvg, iconWidth: 14, getPrefix: Messages.errorToastPrefix, chipCSSVariables: ErrorChipCSSVariables },
};

const iconSpacing = TypeStyle.style({
  $nest: {
    '& > :first-child': { marginRight: 4 },
  }
});

export interface FeedbackChipProps extends ChipProps {
  /** Rendered as the `aria-label` of the chip, along with the type prefix, e.g. `Error: ${message}`. */
  message: string;
}

/** Takes in a resource, returns a react function component for that type of chip. */
const FeedbackChipFactory = (resource: FeedbackChipResource) => {
  const feedbackChipStyleName = TypeStyle.style({ ...resource.chipCSSVariables as React.CSSProperties });

  const FeedbackChip = React.forwardRef<HTMLDivElement, FeedbackChipProps & React.HTMLAttributes<HTMLElement>>((props, ref) => {
    const { children, message, ...chipProps } = props;
    const ariaLabel = `${resource.getPrefix()}${message}`;
    return <Chip
      ref={ref}
      tooltip={message}
      {...chipProps}
      aria-label={ariaLabel}
      className={TypeStyle.classes(props.className, feedbackChipStyleName, iconSpacing)}
    >
      {resource.icon}
      {children}
    </Chip>;
  });
  FeedbackChip.displayName = 'FeedbackChip';
  return FeedbackChip;
}

// export const SuccessChip = FeedbackChipFactory(ResourceMap.success);
// SuccessChip.displayName = 'SuccessChip';
// export const InfoChip = FeedbackChipFactory(ResourceMap.info);
// InfoChip.displayName = 'InfoChip';
// export const WarningChip = FeedbackChipFactory(ResourceMap.warning);
// WarningChip.displayName = 'WarningChip';
export const ErrorChip = FeedbackChipFactory(ResourceMap.error);
ErrorChip.displayName = 'ErrorChip';
